import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Handshake, LayoutGrid, Loader2, Phone, Wrench, FileText } from "lucide-react";
import { EASE } from "@/components/motion/Reveal";
import { COMPANY } from "@/data/site";
import { submitChatLead } from "@/components/chat/chatApi";
import { cn } from "@/lib/utils";

export const Orb = ({ className = "h-10 w-10", spin = true }) => (
  <span className={cn("relative grid shrink-0 place-items-center rounded-full", className)}>
    <span className={cn("orb absolute inset-0 rounded-full", spin && "orb-spin")} />
    <span className="absolute inset-[2px] rounded-full bg-ink-950" />
    <span className="orb-core relative h-[38%] w-[38%] rounded-full" />
  </span>
);

const inline = (text, onNav) =>
  text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g).map((p, k) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={k} className="font-semibold text-white">{p.slice(2, -2)}</strong>;
    const m = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (m) {
      const cls = "font-medium text-volt underline decoration-volt/40 underline-offset-4 hover:decoration-volt";
      return m[2].startsWith("/") ? <Link key={k} to={m[2]} onClick={onNav} className={cls}>{m[1]}</Link> : <a key={k} href={m[2]} target={m[2].startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={cls}>{m[1]}</a>;
    }
    return p;
  });

export const Markdown = ({ text, onNav }) => (
  <div className="space-y-1.5">
    {text.split("\n").map((line, i) => {
      const t = line.trim();
      if (!t) return <span key={i} className="block h-1" />;
      if (/^[-•*]\s/.test(t))
        return (
          <span key={i} className="flex gap-2.5">
            <span className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rotate-45 bg-volt" />
            <span>{inline(t.replace(/^[-•*]\s/, ""), onNav)}</span>
          </span>
        );
      return <span key={i} className="block">{inline(t, onNav)}</span>;
    })}
  </div>
);

export const Typing = () => (
  <span className="flex items-center gap-1.5 py-1" data-testid="chat-typing">
    {[0, 1, 2].map((d) => (
      <motion.span key={d} className="h-1.5 w-1.5 rounded-full bg-volt" animate={{ y: [0, -4, 0], opacity: [0.3, 1, 0.3] }} transition={{ duration: 0.9, repeat: Infinity, delay: d * 0.15 }} />
    ))}
  </span>
);

const ACTIONS = {
  partner: { label: "Become a Partner", to: "/partner", icon: Handshake },
  equipment: { label: "Browse Equipment", to: "/equipment", icon: LayoutGrid },
  service: { label: "Request Service", to: "/service", icon: Wrench },
  contact: { label: "Get Equipment Options", to: "/contact", icon: FileText },
  call: { label: `Call ${COMPANY.phone}`, href: COMPANY.tel, icon: Phone },
};

export const ActionRow = ({ actions, onNav }) => {
  const list = actions.filter((a) => ACTIONS[a]);
  if (!list.length) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }} className="flex flex-wrap gap-2 pl-10" data-testid="chat-actions">
      {list.map((a) => {
        const { label, to, href, icon: Icon } = ACTIONS[a];
        const cls = "group inline-flex items-center gap-2 rounded-full border border-volt/30 bg-volt/[0.07] px-3.5 py-2 text-xs font-semibold text-white transition-[background-color,border-color] hover:border-volt hover:bg-volt/15";
        const inner = <><Icon className="h-3.5 w-3.5 text-volt" />{label}<ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" /></>;
        return to ? <Link key={a} to={to} onClick={onNav} className={cls} data-testid={`chat-action-${a}`}>{inner}</Link> : <a key={a} href={href} className={cls} data-testid={`chat-action-${a}`}>{inner}</a>;
      })}
    </motion.div>
  );
};

export const Suggestions = ({ items, onPick }) =>
  items.length ? (
    <div className="flex flex-wrap gap-2 pl-10" data-testid="chat-followups">
      {items.map((s, i) => (
        <motion.button key={s} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.07 }} onClick={() => onPick(s)} className="rounded-full border border-white/10 px-3 py-1.5 text-left text-xs text-slate-300 transition-colors hover:border-white/40 hover:text-white" data-testid={`chat-followup-${i + 1}`}>
          {s}
        </motion.button>
      ))}
    </div>
  ) : null;

export const LeadSuccess = () => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="ml-10 flex items-center gap-3 rounded-2xl border border-volt/30 bg-volt/[0.06] p-4 text-sm text-white" data-testid="chat-lead-success">
    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-volt text-ink-950"><Check className="h-4 w-4" /></span> Got it — a Brewer specialist will call you soon.
  </motion.div>
);

export const LeadCard = ({ session, onDone }) => {
  const [f, setF] = useState({ name: "", phone: "", business: "" });
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      await submitChatLead({ session_id: session, ...f, interest: "From Brewer AI chat" });
      onDone();
    } catch {
      setState("error");
    }
  };
  const input = "h-10 w-full rounded-xl border border-white/10 bg-ink-950/60 px-3 text-sm text-white placeholder:text-slate-600 focus:border-volt focus:outline-none";
  return (
    <motion.form initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="ml-10 space-y-2.5 rounded-2xl border border-white/10 bg-ink-800 p-4" data-testid="chat-lead-card">
      <p className="font-display text-sm font-semibold text-white">Want a Brewer specialist to reach out?</p>
      <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" className={input} data-testid="chat-lead-name" />
      <input required minLength={7} type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone number" className={input} data-testid="chat-lead-phone" />
      <input value={f.business} onChange={(e) => setF({ ...f, business: e.target.value })} placeholder="Business name (optional)" className={input} data-testid="chat-lead-business" />
      <button type="submit" disabled={state === "loading"} className="btn-primary h-10 w-full text-sm" data-testid="chat-lead-submit">
        {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request a Call"}
      </button>
      {state === "error" && <p className="text-xs text-signal">Couldn't send — please call {COMPANY.phone}.</p>}
    </motion.form>
  );
};
